/**
 * What on-device OCR read from the image: progress, the outcome, values to apply with one tap and
 * the raw text. Pure UI; the page decides what "Usa" does.
 */
import type { Extraction } from "@/domain/model";
import type { FieldSuggestion } from "@/domain/ocr/merge";
import { Button, Notice } from "./primitives";

const FIELD_LABELS: Record<FieldSuggestion["field"], string> = {
  amount: "Importo",
  purchaseDate: "Data",
  merchant: "Esercente",
  categoryId: "Categoria",
  paymentMethodId: "Pagamento",
};

export function ReceiptReading({
  extraction,
  progress,
  suggestions,
  describe,
  onUse,
  onRead,
}: {
  /** undefined while loading; null when the image has not been read. */
  extraction: Extraction | null | undefined;
  /** 0–1 while reading, null otherwise. */
  progress: number | null;
  suggestions: FieldSuggestion[];
  /** Display text of a suggested value (category name, method name, formatted date…). */
  describe: (s: FieldSuggestion) => string;
  onUse?: (s: FieldSuggestion) => void;
  /** Starts a read (force = read again). Omitted when the image cannot be read now. */
  onRead?: (force: boolean) => void;
}) {
  if (progress !== null) {
    const pct = Math.round(progress * 100);
    return (
      <div className="mt-3 flex flex-col gap-2" aria-live="polite">
        <p className="text-sm">Lettura della ricevuta sul dispositivo… {pct}%</p>
        <div role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Lettura della ricevuta" className="h-2 overflow-hidden rounded-full bg-ground">
          <div className="h-full rounded-full bg-petrol transition-[width]" style={{ width: `${Math.max(4, pct)}%` }} />
        </div>
        <p className="text-xs text-muted">Puoi già compilare i campi: i valori letti non sovrascrivono quello che scrivi.</p>
      </div>
    );
  }
  if (extraction === undefined) return null;
  if (extraction === null) {
    return onRead ? (
      <div className="mt-3">
        <Button variant="secondary" onClick={() => onRead(false)}>
          Leggi la ricevuta
        </Button>
      </div>
    ) : null;
  }

  const r = extraction.reading;
  const nothing = extraction.status === "done" && !r?.amount && !r?.date && !r?.merchant;
  return (
    <div className="mt-3 flex flex-col gap-3" data-testid="receipt-reading" aria-live="polite">
      {extraction.status === "failed" ? (
        <Notice tone="error">Non sono riuscito a leggere l&apos;immagine ({extraction.error}). Inserisci i dati a mano.</Notice>
      ) : nothing ? (
        <Notice>Non ho trovato importo, data o esercente nell&apos;immagine: inserisci i dati a mano.</Notice>
      ) : (
        <p className="text-sm">
          <strong>Letto dalla ricevuta.</strong> Controlla i valori prima di salvare: i campi compilati automaticamente sono indicati con «dalla ricevuta».
        </p>
      )}

      {suggestions.length > 0 && onUse ? (
        <ul className="flex flex-col gap-2" aria-label="Valori letti da usare">
          {suggestions.map((s) => (
            <li key={s.field} className="flex items-center justify-between gap-3 rounded-lg border border-line bg-surface px-3 py-2 text-sm" data-testid={`suggestion-${s.field}`}>
              <span>
                <span className="text-muted">{FIELD_LABELS[s.field]}: </span>
                <strong>{describe(s)}</strong>
                {s.confidence === "low" ? <span className="text-muted"> (lettura incerta)</span> : null}
              </span>
              <Button variant="ghost" className="min-h-9 px-3 py-1" onClick={() => onUse(s)}>
                Usa
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      {extraction.text.trim() ? (
        <details className="text-sm">
          <summary className="cursor-pointer text-muted">Testo letto</summary>
          <pre className="mt-2 max-h-60 overflow-auto rounded-lg bg-ground p-3 text-xs whitespace-pre-wrap" data-testid="ocr-text">
            {extraction.text}
          </pre>
        </details>
      ) : null}
      {onRead ? (
        <div>
          <Button variant="ghost" className="px-0" onClick={() => onRead(true)}>
            Rileggi l&apos;immagine
          </Button>
        </div>
      ) : null}
    </div>
  );
}
